import axios from 'axios';
import { reactive, readonly } from 'vue';

// Shared reactive state across all instances
const currentMetadata = reactive({
    title: '',
    description: '',
    image: '',
    noIndex: false
});

/**
 * Get default metadata from localStorage
 */
const getDefaultMetadata = () => {
    const defaultTitle = localStorage.getItem('meta_title') || 'Mayesh';
    const defaultDescription = localStorage.getItem('meta_description') || '';
    const brandSuffix = ' | ' + localStorage.getItem('meta_title_brand') || 'Mayesh';
    const image = localStorage.getItem('company_logo') || '';
    
    return {
        title: defaultTitle,
        description: defaultDescription,
        brandSuffix,
        image: window.location.origin + image
    };
};

/**
 * Generate meta tags for head
 */
const generateMetaTags = (metaData, route) => {
    const defaults = getDefaultMetadata();
    const title = (metaData.title || defaults.title) + defaults.brandSuffix;
    const description = metaData.description || defaults.description;
    const image = metaData.image || defaults.image;

    const meta = [
        { name: 'title', content: title },
        { name: 'description', content: description },
        { name: 'og:title', property: 'og:title', content: title },
        { name: 'og:description', property: 'og:description', content: description },
        { property: 'og:url', content: window.location.origin + route.fullPath },
        { property: 'og:image', content: image },
        { name: 'twitter:title', property: 'twitter:title', content: title },
        { name: 'twitter:description', property: 'twitter:description', content: description },
        { name: 'twitter:url', property: 'twitter:url', content: window.location.origin + route.fullPath },
        { name: 'twitter:image', property: 'twitter:image', content: image },
        { itemprop: 'name', content: title, key: 'itemprop' }
    ];

    if (metaData.noIndex) {
        meta.push({
            name: 'robots',
            property: 'robots',
            content: 'noindex,nofollow'
        });
    }

    return {
        title: title,
        titleTemplate: title,
        meta
    };
};

/**
 * Composable for managing page metadata using Composition API
 * @returns {Object} Metadata management methods and state
 */
export function usePageMetadata() {
    /**
     * Set metadata and generate head data
     */
    const setMetadata = (newMetadata, route) => {
        Object.assign(currentMetadata, { ...currentMetadata, ...newMetadata });
        const headData = generateMetaTags(currentMetadata, route);
        return headData;
    };

    /**
     * Update metadata
     */
    const updateMetadata = (metadata, route) => {
        return setMetadata(metadata, route);
    };

    /**
     * Fetch metadata for a route from API
     */
    const fetchMetadataForRoute = async (route) => {
        let headData = {};
        try {
            let slug = '', isShoppingList = false;
            if (route.params && route.params.slug) {
                slug = route.params.slug;
            } else if (route.path !== '/') {
                const pathSegments = route.path.split('/').filter(segment => segment !== '');
                slug = pathSegments[pathSegments.length - 1] || pathSegments[0] || '';  
            }

            if (route.name === 'shoppingList') {
                isShoppingList = true;
            }
            
            if (route.name === 'editProductPlanner') {
                slug = 'edit-product-planner';
            }

            const response = await axios.post(
                process.env.VUE_APP_API_BASE + 'auth/getMetaDataBySlug',
                { 
                    slug: slug,
                    is_shopping_list: isShoppingList,
                },
                {
                    headers: {
                        'Content-type': 'application/json',
                        Authorization: 'Bearer ' + localStorage.getItem('token'),
                    },
                }
            );

            if (response.data.success && response.data.data.meta_data) {
                headData = setMetadata({
                    title: response.data.data.meta_data.title,
                    description: response.data.data.meta_data.description,
                    noIndex: !!response.data.data.meta_data.no_index
                }, route);
            } else {
                headData = setMetadata({}, route);
            }
        } catch (error) {
            console.log('Error fetching metadata:', error);
            headData = setMetadata({}, route);
        }
        return headData;
    };

    return {
        metadata: readonly(currentMetadata),
        updateMetadata,
        fetchMetadataForRoute
    };
}